import React, { type ReactElement } from 'react';

import LabelResolver from '~/components/LabelResolver';

import type { Model } from '~/types/models';

import * as Api from '~/api';

export function isString(val: any): val is string {
  return typeof val === 'string';
}

export function toLabel(
  path: string,
  type: string,
  emptyLabel?: string | undefined,
  labelKey?: string,
): (value: string | string[]) => ReactElement {
  return function toLabelInner(value) {
    if (value === '¤' && emptyLabel) {
      return (
        <span key={value} className='LabelResolver'>
          {emptyLabel}
        </span>
      );
    }
    const promise = Api.cache(path, async () => await Api.index<Model>(`/${path}`, { pageSize: 1000 }));

    if (typeof value === 'string') {
      return <LabelResolver key={value} labelKey={labelKey ?? 'name'} promise={promise} type={type} value={value} />;
    }

    if (Array.isArray(value)) {
      return (
        <div>
          {value.map((id, index) => {
            return <LabelResolver key={index} labelKey={labelKey ?? 'name'} promise={promise} type={type} value={id} />;
          })}
        </div>
      );
    }

    return <span>{value}</span>;
  };
}

export function prefetchOptions<T extends Model>(
  path: string,
  prependItems?: T[],
  sortBy?: string,
  filters?: Record<string, any>,
  discriminator?: string,
): () => Promise<T[]> {
  return async function prefetchOptionsInner() {
    try {
      const response = await Api.cache(
        `${path}${discriminator ?? ''}`,
        async () =>
          await Api.index<T>(`/${path}`, {
            pageSize: 1000,
            sortBy: sortBy ?? 'name',
            ...(filters ?? {}),
          } as any),
      );
      return [...(prependItems ?? []), ...response.items];
    } catch (e) {
      Api.handleError(e);
    }

    return [];
  };
}

export default {
  prefetchOptions,
  toLabel,
};
